import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { faExclamationCircle } from '@fortawesome/pro-regular-svg-icons';
import { faSpinnerThird } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { detachTransaction } from 'api/pledges';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useForceRender } from 'customHooks/useForceRender';
import { accountQueryKeys } from 'queries/account-query-keys';
import { transactionQueryKeys } from 'queries/transaction-query-keys';

const DetachTransaction = ({ id, children }: { id: number; children: React.ReactNode }) => {
    const { pledge_id } = useParams<{
        pledge_id: string;
    }>();
    const pledgeId = parseInt(pledge_id);
    const { accountId } = useCurrentAccount();
    const { key, forceRender } = useForceRender();
    const queryClient = useQueryClient();

    const { mutate: removeTransaction, isPending } = useMutation({
        mutationFn: () => {
            return detachTransaction(accountId, pledgeId, { transaction_id: id });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.pledges(accountId),
                type: 'all',
            });
            queryClient.invalidateQueries({
                queryKey: transactionQueryKeys.transactions(accountId),
                type: 'all',
            });
            forceRender();
        },
    });

    return (
        <AlertDialog.Root key={key} type="error">
            {children}
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content>
                    <AlertDialog.Header>
                        <AlertDialog.Icon customIcon={faExclamationCircle} />
                        <AlertDialog.Close />
                    </AlertDialog.Header>
                    <AlertDialog.Body>
                        <AlertDialog.Title>Unlink transaction?</AlertDialog.Title>
                        <AlertDialog.Description>
                            Are you sure you want to unlink this transaction from this pledge?
                        </AlertDialog.Description>
                    </AlertDialog.Body>
                    <AlertDialog.Footer>
                        <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                        <AlertDialog.Action
                            disabled={isPending}
                            onClick={e => {
                                e.preventDefault();
                                removeTransaction();
                            }}
                            className="flex gap-2"
                        >
                            {isPending && (
                                <FontAwesomeIcon icon={faSpinnerThird} className="animate-spin" />
                            )}
                            Remove
                        </AlertDialog.Action>
                    </AlertDialog.Footer>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

const Root = DetachTransaction;
const DetachTransactionTrigger = AlertDialog.Trigger;
const Trigger = DetachTransactionTrigger;

export {
    Root,
    Trigger,
    //
    DetachTransaction,
    DetachTransactionTrigger,
};
